import { Component, OnInit, AfterViewInit, Input, ViewChild, OnDestroy } from '@angular/core';
import { select, NgRedux } from '@angular-redux/store';
import { Observable, Subscription as RxSubscription } from 'rxjs';
import { ApiResponseState } from '../../../store/api-response/api-response-state.model';
import { MatSort, MatPaginator, MatTableDataSource } from '@angular/material';
import { ApiResponseDispatcher } from '../../../store/api-response/api-response-dispatcher';
import { TranslationService } from '../../../common/services/languages/translation.service';
import { OrchestrationService } from '../../services/orchestration-service/orchestration.service';
import { Subscription } from './models/subscription.model';
import { SidenavService } from '../../services/sidenav.service';
import { AppState } from '../../../store/state.model';
import { ApiResponseActions } from '../../../store/api-response/api-response-actions';
import { transformDateTime } from '../../../common/helpers/utils';
import { SpinnerService } from '../../../common/components/spinner/overlay-spinner.service';
import { SpinnerRef } from '../../../common/components/spinner/overlay-ref.service';
import { MatButton } from '@angular/material/button';
import { NgIf } from '@angular/common';
import { MatFormField, MatLabel } from '@angular/material/select';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { MatPaginator as MatPaginator_1 } from '@angular/material/paginator';
import { MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow } from '@angular/material/table';
import { MatSort as MatSort_1, MatSortHeader } from '@angular/material/sort';
import { TranslatePipe } from '@ngx-translate/core';
/**
 * Component to display and manage subscription history for a vehicle.
 * Handles fetching subscription history, pagination, filtering, and displaying customer email info.
 */
@Component({
    selector: 'app-subscription-history',
    templateUrl: './subscription-history.component.html',
    styleUrls: ['./subscription-history.component.scss'],
    imports: [MatButton, NgIf, MatFormField, MatLabel, MatInput, FormsModule, MatPaginator_1, MatTable, MatSort_1, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatSortHeader, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, TranslatePipe]
})
export class SubscriptionHistoryComponent implements OnInit, AfterViewInit, OnDestroy {
  /** Flag to control visibility of the subscription history table */
  public showTable = true;

  /** Columns displayed in the subscription history table */
  public displayedColumns: string[] = ['action', 'createDate', 'description', 'source', 'contractID', 'email'];

  /** Data source for the Angular Material table */
  public dataSource: MatTableDataSource<any> = new MatTableDataSource([]);

  /** Total number of subscription history records */
  public totalCount = 0;

  /** Number of records per page */
  public pageSize = 25;

  /** Current page index */
  public currentPage = 0;

  /**
   * Maximum number of records to fetch in a single API call.
   */
  private readonly MAX_FETCH_LIMIT = 10000;

  /** Paginator reference for the table */
  @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;

  /** Sort reference for the table */
  @ViewChild(MatSort, { static: false }) sort: MatSort;

  /** Observable for API response state */
  @select('api') readonly apiObservable: Observable<ApiResponseState>;

  /** Input vehicle details object */
  @Input() vehicleDetails: any;

  /** Subscription to the API observable */
  private apiSubscription: RxSubscription;

  /** Subscription history customer data */
  private subscriptionHistoryCustomer;

  /** Array of unique subscriber GUIDs */
  private uniqueGUID: Array<any> = [];

  /** List of all subscriber GUIDs */
  private listGUID: any[] = [];

  /** Reference to the spinner overlay */
  private spinnerRef: SpinnerRef | null = null;

  /** Flag indicating if there are more GUIDs to process (kept for legacy reset compatibility) */
  private uniqueGUIDFlag: boolean = false;

  /** Array holding subscription history records */
  public subscriptionHistory: Array<any> = [];

  /** Flag indicating if subscription history response has been received */
  public checkSubHistoryResponse: boolean = false;

  /** Filter string for table filtering */
  public filter: string = '';

  /** Cache to store customer info by GUID to prevent duplicate API calls */
  private customerInfoCache: Map<string, any> = new Map();

  /** Set to track GUIDs currently being fetched */
  private fetchingGUIDs: Set<string> = new Set();

  /** Flag to track if customer info has been fetched */
  private customerInfoFetched: boolean = false;

  /** Error message to display when history fetch fails */
  public historyErrorMessage: string = '';

  /** Flag to indicate if there was an error fetching history */
  public hasHistoryError: boolean = false;

  /**
   * Flag indicating that this component has initiated a fetch. Used to ignore
   * the stale Redux state replayed at subscription time (which would otherwise
   * pollute the table with the previous VIN's data or trigger spurious updates).
   */
  private fetchInitiated: boolean = false;

  /**
   * Index tracking which GUID in uniqueGUID is currently being fetched sequentially.
   * Because the Redux store holds only one customer response at a time, we process
   * one GUID at a time and use this index to know which response just arrived.
   */
  private currentGUIDIndex: number = 0;

  /**
   * Constructor to inject required services.
   * @param orch Orchestration service for API calls
   * @param translate Translation service for i18n
   * @param apiDispatcher Dispatcher for API actions
   * @param sidenav Service to control side navigation panel
   * @param ngRedux Redux store service
   * @param spinner Spinner service for loading overlays
   */
  constructor(
    public orch: OrchestrationService,
    public translate: TranslationService,
    public apiDispatcher: ApiResponseDispatcher,
    private sidenav: SidenavService,
    private ngRedux: NgRedux<AppState>,
    public spinner: SpinnerService,
  ) { }

  /**
   * Angular lifecycle hook called on component initialization.
   * Subscribes to API observable and initiates fetching subscription history.
   */
  ngOnInit() {
    // Set up the sortingDataAccessor before any data is loaded
    this.dataSource.sortingDataAccessor = (item, property) => {
      if (property === 'createDate') {
        return item.createDateRaw;
      }
      return item[property] || '';
    };
    this.apiSubscription = this.apiObservable.subscribe((api) => {
      if (!this.fetchInitiated) {
        return;
      }
      if (api.apiAction === ApiResponseActions.SET_SUBSCRIPTION_HISTORY) {
        const subHistory = api.subscriptionHistory as any;

        if (subHistory?.type === 'success') {
          const payload = subHistory['payload'] || {};
          const historyItems = payload['subscriptionHistory'] || [];
          this.subscriptionHistory = Array.isArray(historyItems) ? historyItems : [];
          this.totalCount = payload['totalCount'] || 0;

          this.checkSubHistoryResponse = true;
          this.hasHistoryError = false;
          this.historyErrorMessage = '';
          this.listGUID = [];
          this.uniqueGUID = [];
          this.customerInfoCache.clear();
          this.fetchingGUIDs.clear();
          this.currentGUIDIndex = 0;

          if (this.subscriptionHistory.length > 0) {
            this.setGUID();

            this.setEmailsForAllRecords();
            this.setTableData();

            this.fetchNextCustomerEmail();
          } else {
            this.setTableData();
          }
        } else if (subHistory && subHistory['type'] === 'error') {
          this.checkSubHistoryResponse = true;
          this.hasHistoryError = true;
          this.subscriptionHistory = [];
          this.totalCount = 0;

          const errorPayload = subHistory['payload'];
          if (errorPayload && errorPayload['status'] === 429) {
            this.historyErrorMessage = this.translate.getCustomTranslation('subscription-details.errors.too-many-requests')['value']
              || 'Too many requests. Please try again later.';
          } else if (errorPayload && errorPayload['message']) {
            this.historyErrorMessage = errorPayload['message'];
          } else {
            this.historyErrorMessage = this.translate.getCustomTranslation('subscription-details.errors.history-fetch-failed')['value']
              || 'Failed to fetch subscription history. Please try again.';
          }

          this.setTableData();
        }
      }

      this.subscriptionHistoryCustomer = this.ngRedux.getState().api.subscriptionHistoryCustomer;
      if (this.checkSubHistoryResponse &&
        this.subscriptionHistoryCustomer &&
        this.subscriptionHistoryCustomer.state) {
        const currentGuid = this.uniqueGUID[this.currentGUIDIndex];

        if (!currentGuid || !this.fetchingGUIDs.has(currentGuid)) {
          return;
        }

        const state = this.subscriptionHistoryCustomer.state;

        if (state.type === 'success') {
          const customerData = state.customer;
          this.customerInfoCache.set(currentGuid, customerData);
        } else if (state.type === 'error') {
          this.customerInfoCache.set(currentGuid, null);
        } else {
          return;
        }

        this.fetchingGUIDs.delete(currentGuid);
        this.currentGUIDIndex++;
        this.setEmailsForAllRecords();
        this.setTableData();
        this.fetchNextCustomerEmail();
      }
    });
    this.fetchInitiated = true;
    this.fetchHistory();
  }

  /**
   * Angular lifecycle hook called after view initialization.
   * Initializes sort connection.
   */
  ngAfterViewInit() {
    this.initializeSort();
  }

  /**
   * Initializes or re-initializes the sort and paginator connections.
   */
  private initializeSort(): void {
    // Use setTimeout to ensure the view is fully rendered after *ngIf changes
    setTimeout(() => {
      if (this.dataSource) {
        if (this.sort) {
          this.dataSource.sort = this.sort;
          this.sort.disableClear = true;


          this.sort.active = 'createDate';
          this.sort.direction = 'desc';
          this.sort.sortChange.emit({ active: 'createDate', direction: 'desc' });
        }
        if (this.paginator) {
          this.dataSource.paginator = this.paginator;
        }
      }
    });
  }

  /**
   * Dispatches the request to fetch subscription history from the Orchestration Service.
   * Fetches all records in a single call so sorting and filtering apply across
   */
  private fetchHistory() {
    this.openSpinner();
    const vin = this.vehicleDetails['vin'];

    this.orch.getSubscriptionHistory(vin, 1, this.MAX_FETCH_LIMIT);
  }

  /**
   * Handles pagination changes triggered by user interaction.
   * Pagination is fully client-side; no new API call is needed.
   * @param event Pagination event containing pageIndex and pageSize
   */
  public onPageChange(event: any) {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
  }

  /**
   * Applies a filter string to the table data source.
   * @param filterValue The filter string entered by the user
   */
  public applyFilter(filterValue: string) {
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  /**
   * Formats and sets the subscription history data into the table data source.
   * Also manages table visibility and sorting.
   */
  private setTableData(): void {
    const formattedData = this.formatTableData(this.subscriptionHistory);
    this.showTable = formattedData && formattedData.length > 0;

    // Assign the data
    this.dataSource.data = formattedData;

    // Re-initialize sort after data is loaded (handles *ngIf recreation)
    this.initializeSort();

    this.closeSpinner();
  }


  /**
   * Extracts unique subscriber GUIDs from the subscription history.
   */
  private setGUID(): void {
    for (const customerRecord in this.subscriptionHistory) {
      if (Object.prototype.hasOwnProperty.call(this.subscriptionHistory, customerRecord)) {
        const guid = this.subscriptionHistory[customerRecord].subscriberGuid;
        if (guid) {
          this.listGUID.push(guid);
        }
      }
    }
    this.uniqueGUID = this.listGUID.filter((value, index, self) => self.indexOf(value) === index);
  }

  /**
   * Fetches customer email for the next GUID in the sequential queue.
   *
   * Because the Redux store only holds a single customer response at a time,
   * we dispatch calls one-by-one. After each response is handled in ngOnInit,
   * currentGUIDIndex is incremented and this method is called again to continue
   * the chain until all unique GUIDs have been resolved.
   *
   * GUIDs that are already cached are skipped automatically.
   */
  private fetchNextCustomerEmail(): void {
    while (
      this.currentGUIDIndex < this.uniqueGUID.length &&
      this.customerInfoCache.has(this.uniqueGUID[this.currentGUIDIndex])
    ) {
      this.currentGUIDIndex++;
    }

    if (this.currentGUIDIndex >= this.uniqueGUID.length) {
      this.setEmailsForAllRecords();
      this.setTableData();
      return;
    }

    const guid = this.uniqueGUID[this.currentGUIDIndex];
    this.fetchingGUIDs.add(guid);
    this.orch.searchCustomer('subscriptionHistoryCustomer', { guid });
  }

  /**
   * Sets the email address on each subscription history record based on its subscriber GUID.
   * Uses cached customer info or a fallback translation string if not yet resolved.
   */
  private setEmailsForAllRecords(): void {
    for (const record of this.subscriptionHistory) {
      const guid = record.subscriberGuid;
      const customerData = guid ? this.customerInfoCache.get(guid) : null;
      const emailAddress =
        customerData && customerData.emails && customerData.emails[0]
          ? customerData.emails[0].emailAddress
          : this.translate.getCustomTranslation('subscription-details.errors.customer-not-found')['value'];
      record.email = emailAddress;
    }
  }

  /**
   * Formats raw subscription history data for display in the table.
   * @param data Raw subscription history array
   * @returns Formatted array with transformed dates and descriptions
   */
  private formatTableData(data: any[]) : any[] {
    return data.map((element: any ) => {
      return {
        action: element['action'],
        createDate: transformDateTime(element['createDate']),
        createDateRaw: new Date(element['createDate']),
        description: this.getDescription(element['action'], element['description'], element['detailedDescription']),
        source: element['subscription']['updateSource'] || element['subscription']['createSource'],
        contractID: element['subscription']['contractID'],
        email: element.email
      };
    });
  }

  /**
   * Opens the loading spinner overlay with a message.
   */
  private openSpinner(): void {
    if (!this.spinnerRef) {
      this.spinnerRef = this.spinner.open({ message: { value: 'Loading History...' } });
    }
  }

  /**
   * Closes the loading spinner overlay if open.
   */
  private closeSpinner(): void {
    if (this.spinnerRef) {
      this.spinnerRef.close();
      this.spinnerRef = null;
    }
  }

  /**
   * Closes the side navigation panel and resets the component.
   */
  public close(): void {
    this.sidenav.close();
    this.reset();
  }

  /**
   * Resets the component state to initial values.
   * Clears subscription history data, pagination settings, filters, and flags.
   * Ensures the component starts fresh when reopened.
   */
  private reset(): void {
    this.currentPage = 0;
    this.pageSize = 25;
    this.dataSource.data = [];
    this.subscriptionHistory = [];
    this.checkSubHistoryResponse = false;
    this.filter = '';
    this.uniqueGUID = [];
    this.listGUID = [];
    this.uniqueGUIDFlag = false;
    this.currentGUIDIndex = 0;
    this.fetchingGUIDs.clear();
    this.customerInfoCache.clear();
    this.customerInfoFetched = false;
    this.hasHistoryError = false;
    this.historyErrorMessage = '';

    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
  }

  /**
   * Angular lifecycle hook called on component destruction.
   * Unsubscribes from API observable to prevent memory leaks.
   */
  ngOnDestroy() {
    if (this.apiSubscription) {
      this.apiSubscription.unsubscribe();
    }
  }

  /**
   * Returns a translated description string for a subscription history entry.
   * If detailedDescription is present, returns it directly.
   * Otherwise, constructs a translation key from action and description.
   * @param action Action string
   * @param description Description string
   * @param detailedDescription Detailed description string (optional)
   * @returns Translated description string
   */
  private getDescription(action: string, description: string, detailedDescription: string): string {
    if (!detailedDescription) {
      const descriptionString = action.toLowerCase() + '-' + description.replace(/ /g, '-').toLowerCase();
      return this.translate.getCustomTranslation(`subscription-details.history-description.${descriptionString}`)['value'];
    }
    return detailedDescription;
  }
  /**
   * @function getCheckSubHistoryResponse gets the status of the subHistory response
   **/
  public getCheckSubHistoryResponse(): boolean {
    return this.checkSubHistoryResponse;
  }
}
